import moment from 'moment';

import { ActionRequiredItemEnum, SupervisionType } from '../../../constants';
import {
  FORTA_ERROR,
  FORTA_SUCCESS,
  FORTA_ORANGE,
  FORTA_GRAY3,
  FORTA_DOCS,
} from '../../../utils/colors';
import { capitalizeFirstLetter } from '../../../helpers';

import type { IClientParentSNUtilization, WeeklyUtilization } from './ClientInsights';
import type {
  DocumentAggregation,
  IClient,
  IClientFamily,
  IDiagnosis,
  IProviderAssignment,
  ISibling,
  ITherapyUtilization,
  ITreatmentPlan,
} from '../../../utils/types';
import type { ClinicalRelationships } from '../../../constants';
import type { Moment } from 'moment';
import type { TFunction } from 'i18next';

export const NOT_FOUND = 'Not found';
export const NO_ACTIVE_TP_FOUND = 'No active TP found';
export const NO_BT_ASSIGNED = 'No BT(s) assigned';

export const pathColor = (value: number) => {
  if (value < 25) return FORTA_ERROR;
  if (value <= 50) return FORTA_ORANGE;
  if (value > 50) return FORTA_SUCCESS;
};

export const supervisionTypeStyles = {
  [SupervisionType.Parent]: {
    name: 'Parent supervision (97156)',
    color: FORTA_DOCS.PSN_56,
  },
  [SupervisionType.Client]: {
    name: 'Client supervision (97155)',
    color: FORTA_DOCS.CSN_55,
  },
  [SupervisionType.Bt]: {
    name: 'BT supervision',
    color: FORTA_DOCS.BTSN,
  },
  [SupervisionType.Assessment]: {
    name: 'Assessment notes (97151)',
    color: FORTA_DOCS.AN_51,
  },
  [SupervisionType.Assessment2]: {
    name: 'Assessment notes (97152)',
    color: FORTA_DOCS.AN_52,
  },
  [SupervisionType.GroupParent]: {
    name: 'Group parent training (97157)',
    color: FORTA_DOCS.GPSN_57,
  },
  [ActionRequiredItemEnum.TreatmentPlan]: {
    name: 'Treatment plan',
    color: FORTA_DOCS.TP,
  },

  [ActionRequiredItemEnum.Session]: {
    name: 'Session',
    color: FORTA_DOCS.SN_53,
  },
};

export const isDateOlderThan24Hours = (date: string | Date | Moment) => {
  const oneDay = 24 * 60 * 60 * 1000;
  const dateToCompare = date instanceof Date ? date : new Date(date.toString());
  return dateToCompare.getTime() + oneDay < new Date().getTime();
};
export function getChartColor(utilization: ITherapyUtilization | null) {
  if (!utilization) return FORTA_GRAY3;
  if (utilization.utilization <= 50) {
    return FORTA_ERROR;
  }
  if (utilization.utilization > 50 && utilization.utilization <= 80) {
    return FORTA_ORANGE;
  }
  if (utilization.utilization > 80) {
    return FORTA_SUCCESS;
  }
  return FORTA_GRAY3;
}

export function getHelperText1(t: TFunction<'dashboard'>, utilization: number) {
  if (!utilization && utilization !== 0) return '';
  if (utilization <= 50) {
    const options = [
      t('utilization.currentWeek.helperText1'),
      t('utilization.currentWeek.helperText2'),
    ];
    const index = Math.round(Math.random());
    return options[index];
  }
  if (utilization > 50 && utilization <= 80) {
    return t('utilization.currentWeek.helperText3');
  }
  if (utilization > 80) {
    return t('utilization.currentWeek.helperText4');
  }
  return '';
}

export function getHelperText2(t: TFunction<'dashboard'>, utilization: number) {
  if (!utilization && utilization !== 0) return '';
  if (utilization <= 50) {
    return [t('utilization.currentWeek.helperText5'), t('utilization.currentWeek.helperText6')][
      Math.round(Math.random())
    ];
  }
  if (utilization > 50 && utilization <= 80) {
    return t('utilization.currentWeek.helperText7');
  }
  if (utilization > 80) {
    return '';
  }
  return '';
}

export function isMonthSame(startDate: string, endDate: string) {
  return moment(startDate).format('MMMM') === moment(endDate).format('MMMM');
}

export function processClientParentSNUtilizations(
  clientSNAggregations: DocumentAggregation[],
  parentSNAggregations: DocumentAggregation[],
  dateArray: string[][],
) {
  const data: IClientParentSNUtilization = {};
  dateArray.forEach((dates, i) => {
    data[dates[0]] = {
      parentSN: parentSNAggregations[i]?.duration,
      clientSN: clientSNAggregations[i]?.duration,
    };
  });

  return data;
}

export function mergeClientParentSessionUtilization(
  clientParentSNUtilization: IClientParentSNUtilization,
  sessionUtilization: ITherapyUtilization[],
) {
  const utilization: WeeklyUtilization[] = [];
  Object.entries(clientParentSNUtilization).forEach(([key, value]) => {
    const sessionUtil = sessionUtilization.find((util) => util.week_starts_on === key);
    const data = {
      name: moment(key).format('MMM Do'),
      parentSN: value.parentSN ?? 0,
      clientSN: value.clientSN ?? 0,
      session: sessionUtil ? sessionUtil.hours_completed * 3600 : 0, // Convert in seconds
      clientSNExpectedHours: sessionUtil
        ? Math.max(sessionUtil.hours_completed * 0.05 * 3600, 900)
        : 0, // Max of 15 mins or 5% of session hours completed in seconds
      sessionRecommendation: sessionUtil ? sessionUtil.hours_allowed : 0,
    };
    utilization.push(data);
  });
  return utilization;
}

export const getSiblingLabels = (
  treatmentPlanInfo: ITreatmentPlan | null,
  activeTPId?: number | null,
) => {
  if (!treatmentPlanInfo || !treatmentPlanInfo?.client_and_family_background?.siblings.length) {
    return ['No siblings found'];
  }
  if (!activeTPId) return [NO_ACTIVE_TP_FOUND];
  return treatmentPlanInfo?.client_and_family_background?.siblings.map(
    (sibling: ISibling) => sibling.name,
  );
};

export const getLanguageLabels = (
  treatmentPlanInfo: ITreatmentPlan | null,
  activeTPId?: number | null,
) => {
  if (!treatmentPlanInfo || !treatmentPlanInfo?.client_and_family_background?.member_language) {
    return [NOT_FOUND];
  }
  if (!activeTPId) return [NO_ACTIVE_TP_FOUND];
  return [capitalizeFirstLetter(treatmentPlanInfo?.client_and_family_background?.member_language)];
};

export const getDiagnosisLabels = (
  treatmentPlanInfo: ITreatmentPlan | null,
  activeTPId?: number | null,
) => {
  if (!treatmentPlanInfo || !treatmentPlanInfo?.medical_history?.diagnoses?.length) {
    return ['No diagnosis found'];
  }
  if (!activeTPId) return [NO_ACTIVE_TP_FOUND];

  return [
    `AHD - ${treatmentPlanInfo?.medical_history?.autism_severity_level}`,
    ...(treatmentPlanInfo?.medical_history?.diagnoses ?? []).map(
      (diagnosis: IDiagnosis) => diagnosis.diagnosis_name.split('(')[0],
    ),
  ];
};

export const getParentsLabels = (clientsFamily: IClientFamily[] | null) => {
  if (!clientsFamily?.length) {
    return [NOT_FOUND];
  }
  const { parents_and_caregivers } = clientsFamily[0];
  return parents_and_caregivers.map(({ name }) => name);
};

export const getBCBAProvierLabels = (
  providerAssignments: IProviderAssignment[],
  type: ClinicalRelationships,
) => {
  const filteredProviders = providerAssignments.filter((p) => p.clinical_relationship === type);
  if (!filteredProviders.length) return [NOT_FOUND];
  return filteredProviders.map((p) => `${p.provider?.first_name} ${p.provider?.last_name}`);
};

export const getBTLabels = (client: IClient | null) => {
  if (!client?.providers?.length) return [NO_BT_ASSIGNED];
  return client.providers.map((provider: any) => `${provider.first_name} ${provider.last_name}`);
};
